import React from "react";
import { Box, Skeleton } from "@mui/material";
import { DotLegalHeader, DotLegalPaper, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import riskTileWave from "./riskTileWave.svg?url";

interface RiskStatTileProps {
    label: string;
    // Zero-based position of this tile in the stat row. Each tile paints its own horizontal slice of a single
    // row-wide wave (background-size 400%/200%), so the wave flows continuously across the tiles instead of
    // each tile restarting the same hump (work item #6717).
    waveIndex: number;
    // Number of tiles in the stat row on md+ (4 without the exposure tile, 5 with it). The wave is sliced
    // across this many columns, so the last tile still gets its share of the wave.
    columnCount?: number;
    // Small line rendered under the label, mirroring the third line on the mitigation papers
    // (e.g. the appetite hint or the overdue count).
    subLabel?: React.ReactNode;
    isLoading?: boolean;
    // Greyed-out "not applicable" state — used for the overdue tile while viewing a point-in-time snapshot.
    disabled?: boolean;
    tooltip?: string;
    // Rendered in the top-right corner (mirrors the dashboard count card's progress ring).
    topRight?: React.ReactNode;
    children: React.ReactNode;
}

// The wave-background stat box used across the top of the risk governance overview, mirroring the annual
// wheel's "Overdue activities" tile (same wave asset + cover sizing so it fills the box). The value content
// (a number, a coloured number, or the distribution bar) is supplied by the caller; this component only owns
// the shared shape, wave background and label.
function RiskStatTile({ label, waveIndex, columnCount = 4, subLabel, isLoading, disabled, tooltip, topRight, children }: RiskStatTileProps) {
    // The stat row is columnCount columns on md+ and 2 columns on smaller screens; each tile shows the 1/n-th
    // horizontal slice of the row-wide wave that lines up with its column, so neighbouring slices join into one wave.
    const wavePositionX = { xs: `${(waveIndex % 2) * 100}%`, md: `${(waveIndex / (columnCount - 1)) * 100}%` };

    return (
        <DotLegalPaper
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                // A minimum, not a fixed height: the control-coverage tile carries two sub-lines and would clip at
                // 125. The stat row is a grid, so whichever tile needs more lifts all of them to the same height.
                // Set to fit the tallest tile (label + value + two sub-lines) so the row keeps one height whatever
                // is on it: the sub-lines are conditional (gap counts drop out at zero, and the mode switches hide
                // whole lines), and without the floor the tiles would resize as data loads or the mode changes.
                minHeight: 140,
                backgroundImage: `url("${riskTileWave}")`,
                backgroundRepeat: "no-repeat",
                backgroundPositionX: wavePositionX,
                backgroundPositionY: "bottom",
                backgroundSize: { xs: "200% 100%", md: `${columnCount * 100}% 100%` },
                ...(disabled ? { filter: "grayscale(1)", opacity: 0.5 } : {}),
            }}
        >
            {topRight && !isLoading && <Box sx={{ position: "absolute", top: 12, right: 12 }}>{topRight}</Box>}
            <DotLegalTooltip text={tooltip}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 36, width: "100%" }}>
                    {isLoading ? <Skeleton variant="text" width={48} height={36} /> : children}
                </Box>
            </DotLegalTooltip>
            <DotLegalHeader headerStyle="small" center marginBottom={0}>
                {label}
            </DotLegalHeader>
            {subLabel && <Box sx={{ textAlign: "center" }}>{subLabel}</Box>}
        </DotLegalPaper>
    );
}

export default RiskStatTile;
